import { resolveId } from '@medplum/core';
import { Patient, PatientContact } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useState } from 'react';

export interface UsePatientContactResult {
  partnersList: PatientContact[];
  partnersListLoading: boolean;
}

export function usePatientContact(patient: Patient | undefined): UsePatientContactResult {
  const medplum = useMedplum();
  const [deletedOrgIds, setDeletedOrgIds] = useState<Set<string>>(new Set());
  const [isChecking, setIsChecking] = useState(false);

  const allPartners = useMemo(() => {
    return patient?.contact?.filter((contact) => contact.organization?.reference) || [];
  }, [patient?.contact]);

  useEffect(() => {
    const checkOrganizations = async () => {
      const orgIds = allPartners.map((p) => resolveId(p.organization!)).filter((id): id is string => !!id);

      if (orgIds.length === 0) {
        setDeletedOrgIds(new Set());
        return;
      }

      setIsChecking(true);
      const deleted = new Set<string>();

      await Promise.all(
        orgIds.map(async (orgId) => {
          try {
            await medplum.readResource('Organization', orgId);
          } catch (error: any) {
            if (error?.outcome?.issue?.[0]?.code === 'deleted') {
              deleted.add(orgId);
            }
          }
        })
      );

      setDeletedOrgIds(deleted);
      setIsChecking(false);
    };

    checkOrganizations();
  }, [allPartners, medplum]);

  const validPartners = useMemo(() => {
    return allPartners.filter((partner) => {
      const orgId = resolveId(partner.organization!);
      return orgId && !deletedOrgIds.has(orgId);
    });
  }, [allPartners, deletedOrgIds]);

  return {
    partnersList: validPartners,
    partnersListLoading: isChecking,
  };
}
